/**
 * The matchup card: two logos, the kickoff, and the score when there is one.
 *
 * Shared by the Home screen and the game view so a matchup looks the same
 * wherever it appears. On a `fill` ground, which means every piece of text in
 * here is `ink` or `ink` at reduced alpha and never `dim`: Steelers gold with
 * `dim` on it measures 1.33:1.
 *
 * `showScore` is off before kickoff rather than rendering zeroes. A card that
 * says 0 to 0 three days early reads as a game that has started badly, not as
 * a game that has not started.
 *
 * The viewer's own team is always on the left, whichever side of the matchup
 * it is on, with `@` or `vs` telling you where the game is played. Anchoring
 * it means you never have to work out which number is yours.
 */

import { Link } from 'react-router-dom'
import type { Game } from '@shared/types'
import { teamAbbr } from '@shared/teams'
import { TeamLogo } from './index'

const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: 'long' })
const MONTH_DAY = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const TIME = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })

function kickoffLine(at: number): string {
  return `${WEEKDAY.format(at)}, ${MONTH_DAY.format(at)} · ${TIME.format(at)}`
}

function periodLabel(period: number | null): string {
  if (!period) return ''
  if (period <= 4) return `Q${period}`
  return period === 5 ? 'OT' : `OT${period - 4}`
}

function statusLine(g: Game): string {
  if (g.state === 'pre') return kickoffLine(g.kickoffAt)
  if (g.state === 'post') return g.wentToOt ? 'Final, OT' : 'Final'
  return [periodLabel(g.period), g.clockDisplay].filter(Boolean).join(' ') || g.statusDetail || 'Live'
}

export function MatchupCard({
  game, myTeamId, eyebrow, to, style,
}: {
  game: Game
  /** Pinned to the left. Falls back to the away team when the viewer has no side. */
  myTeamId?: string | null
  eyebrow?: string
  /** Wraps the card in a link when given. */
  to?: string
  style?: React.CSSProperties
}) {
  const iAmHome = !!myTeamId && game.homeTeamId === myTeamId
  const iAmIn = !!myTeamId && (iAmHome || game.awayTeamId === myTeamId)

  const leftId = iAmIn ? (iAmHome ? game.homeTeamId : game.awayTeamId) : game.awayTeamId
  const rightId = leftId === game.homeTeamId ? game.awayTeamId : game.homeTeamId
  const leftIsHome = leftId === game.homeTeamId

  const showScore = game.state !== 'pre' && game.homeScore !== null && game.awayScore !== null
  const leftScore = leftIsHome ? game.homeScore : game.awayScore
  const rightScore = leftIsHome ? game.awayScore : game.homeScore

  // "@" when the left team is away, "vs" when it is hosting.
  const joiner = leftIsHome ? 'vs' : '@'

  const inner = (
    <div
      style={{
        background: 'var(--fill)',
        color: 'var(--ink)',
        borderRadius: 'var(--radius-card)',
        padding: '16px 18px 18px',
        ...style,
      }}
    >
      {eyebrow ? (
        <div
          className="mono"
          style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', opacity: 0.78 }}
        >
          {eyebrow}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginTop: eyebrow ? 14 : 2,
        }}
      >
        <Side teamId={leftId} score={showScore ? leftScore : null} />
        <div className="mono" style={{ fontSize: 13, fontWeight: 700, opacity: 0.78, flex: '0 0 auto' }}>
          {joiner}
        </div>
        <Side teamId={rightId} score={showScore ? rightScore : null} align="right" />
      </div>

      <div
        style={{ marginTop: 16, fontSize: 13, textAlign: 'center', opacity: 0.82 }}
        aria-live={game.state === 'in' ? 'polite' : undefined}
      >
        {statusLine(game)}
      </div>
    </div>
  )

  if (!to) return inner
  return <Link to={to} style={{ display: 'block', color: 'inherit' }}>{inner}</Link>
}

function Side({
  teamId, score, align = 'left',
}: { teamId: string; score: number | null; align?: 'left' | 'right' }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 0',
        flexDirection: align === 'right' ? 'row-reverse' : 'row',
      }}
    >
      <TeamLogo teamId={teamId} size={48} fallback="none" />
      <div style={{ minWidth: 0, textAlign: align }}>
        <div className="mono" style={{ fontSize: 15, fontWeight: 700 }}>{teamAbbr(teamId)}</div>
        {score !== null ? (
          <div className="mono" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>{score}</div>
        ) : null}
      </div>
    </div>
  )
}
